import { z } from "zod";

import {
  AUTH_CODE_ALPHABET,
  AUTH_CODE_LENGTH,
  GROUP_MODES,
  LIMITS,
  LIST_OCCASIONS,
  normalizeAuthCode,
  GROUP_COLOR_KEYS,
} from "../constants";
import { emailSchema, resourceIdSchema } from "./common";

/**
 * Everything a visitor or an organizer types into a group can end up in a mail sent to
 * someone who never asked for it ("Clément t'invite au Secret Santa « … »"). So nothing
 * here is free text: a name is letters, a title is a short label, and neither can carry
 * a link, an address or a phone number. The server enforces the same schemas.
 */

/** "www.", "http://", "@", or a word followed by a domain-looking suffix. */
const LINKISH = /@|:\/\/|www\.|\.[a-z]{2,}(?:\b|\/)/i;

/**
 * Whether a text carries a link or an address. For free text that is shown on a page but
 * must not travel in a mail to a stranger (a list's title in an invitation): the mail
 * leaves it out rather than refusing it on the page.
 */
export const containsLink = (value: string): boolean => LINKISH.test(value);

const collapse = (value: string) => value.normalize("NFC").replace(/\s+/g, " ").trim();

export const personNameSchema = z
  .string()
  .transform(collapse)
  .pipe(
    z
      .string()
      .min(1, "Un prénom, au moins une lettre.")
      .max(LIMITS.personNameMax)
      .regex(/^[\p{L}\p{M}][\p{L}\p{M}\p{N} '’.-]*$/u, "Des lettres seulement.")
      .refine((value) => !LINKISH.test(value), "Pas de lien ni d'adresse.")
      // A digit or two is a nickname ("Jo2"); a run is a phone number or a code.
      .refine((value) => !/\d{3,}/.test(value), "Pas de numéro."),
  );

export const groupTitleSchema = z
  .string()
  .transform(collapse)
  .pipe(
    z
      .string()
      .min(1, "Donnez un nom au groupe.")
      .max(LIMITS.groupTitleMax)
      .regex(/^[\p{L}\p{M}\p{N} '’.,!&()-]+$/u, "Des lettres, des chiffres et c'est tout.")
      .refine((value) => !LINKISH.test(value), "Pas de lien ni d'adresse.")
      // Up to four digits for the year ("Noël 2027"); more is a phone number.
      .refine((value) => !/\d{5,}/.test(value), "Pas de numéro."),
  );

/**
 * The organizer's note, quoted in every Secret Santa mail. The one piece of free text that
 * reaches other people's inboxes, so it carries nothing they could be sent to: no link, no
 * address, no phone number. A date ("le 25/12") is fine.
 */
export const santaMessageSchema = z
  .string()
  .transform((value) =>
    value
      .normalize("NFC")
      .replace(/[ \t]+/g, " ")
      .trim(),
  )
  .pipe(
    z
      .string()
      .max(LIMITS.santaMessageMax)
      .refine((value) => !LINKISH.test(value), "Pas de lien ni d'adresse.")
      .refine((value) => !/(?:\d[\s.-]?){8,}/.test(value), "Pas de numéro de téléphone."),
  );

/**
 * What a place is called: a first name ("Mamie Jo") in a group of people, a list's name
 * ("Salle à manger") in a collection. Shown to visitors and in mails: short, and never a
 * link or a phone number.
 */
export const placeNameSchema = z
  .string()
  .transform(collapse)
  .pipe(
    z
      .string()
      .min(1, "Un nom, au moins une lettre.")
      .max(LIMITS.personNameMax)
      .regex(
        /^[\p{L}\p{M}\p{N}][\p{L}\p{M}\p{N} '’.,&()-]*$/u,
        "Des lettres et des chiffres seulement.",
      )
      .refine((value) => !LINKISH.test(value), "Pas de lien ni d'adresse.")
      .refine((value) => !/\d{5,}/.test(value), "Pas de numéro."),
  );

/** The key two names collide on: "Léa" and "lea " are one person in a group. */
export const personNameKey = (name: string): string =>
  name.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();

const uniqueNames = (names: string[]) => new Set(names.map(personNameKey)).size === names.length;

export const createGroupSchema = z.object({
  title: groupTitleSchema,
  /** Optional: a group is a set of lists, each with its own occasion and date. */
  occasion: z.enum(LIST_OCCASIONS).default("other"),
  eventDate: z.iso.date().nullable().optional(),
  /** How the organizer signs the page and any mail. */
  organizerName: personNameSchema,
  /**
   * First places, typed by the organizer: people (the organizer too, if they take part),
   * or the lists of a collection.
   */
  places: z
    .array(placeNameSchema)
    .max(LIMITS.placesPerGroup)
    .refine(uniqueNames, "Deux noms identiques : ajoutez une initiale.")
    .default([]),
  mode: z.enum(GROUP_MODES).default("lists"),
});
export type CreateGroupInput = z.input<typeof createGroupSchema>;

export const updateGroupSchema = z
  .object({
    title: groupTitleSchema,
    occasion: z.enum(LIST_OCCASIONS),
    eventDate: z.iso.date().nullable(),
    organizerName: personNameSchema,
    santaBudgetCents: z.number().int().min(100).max(LIMITS.santaBudgetMaxCents).nullable(),
    /** Empty clears it. */
    santaMessage: santaMessageSchema.nullable(),
    avoidPrevious: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
export type UpdateGroupInput = z.input<typeof updateGroupSchema>;

export const addPlaceSchema = z.object({ name: placeNameSchema });
export type AddPlaceInput = z.input<typeof addPlaceSchema>;

export const updatePlaceSchema = z
  .object({
    name: placeNameSchema,
    contactEmail: emailSchema.nullable(),
    /** The person's gommette; null peels it off. */
    color: z.enum(GROUP_COLOR_KEYS).nullable(),
  })
  .partial();
export type UpdatePlaceInput = z.input<typeof updatePlaceSchema>;

/** A visitor taking a place; a signed-in one may bring a list they already have. */
export const claimPlaceSchema = z.object({
  attachListId: resourceIdSchema.optional(),
  /** Set once the visitor agreed to fold the guest behind this place into their account. */
  merge: z.boolean().optional(),
});
export type ClaimPlaceInput = z.input<typeof claimPlaceSchema>;

/** "Je ne suis pas dans la liste": a new place, and the visitor takes it at once. */
export const addSelfSchema = z.object({ name: personNameSchema });
export type AddSelfInput = z.input<typeof addSelfSchema>;

/**
 * A place's personal link, as read from the URL fragment. The token is 43 base64url
 * characters: a SHA-256 HMAC.
 */
export const openPlaceSchema = z.object({
  placeId: resourceIdSchema,
  token: z
    .string()
    .length(43)
    .regex(/^[A-Za-z0-9_-]+$/),
  merge: z.boolean().optional(),
});
export type OpenPlaceInput = z.input<typeof openPlaceSchema>;

export const placeCodeSchema = z.object({
  code: z
    .string()
    .transform(normalizeAuthCode)
    .pipe(
      z
        .string()
        .length(AUTH_CODE_LENGTH)
        .regex(new RegExp(`^[${AUTH_CODE_ALPHABET}]+$`), "Le code ne contient que des chiffres."),
    ),
});
export type PlaceCodeInput = z.input<typeof placeCodeSchema>;

export const attachListSchema = z.object({ listId: resourceIdSchema });
export type AttachListInput = z.input<typeof attachListSchema>;

/** Who may not draw whom, both ways (a couple). Gommettes cover the rest: colorRulesSchema. */
export const exclusionsSchema = z.object({
  pairs: z
    .array(z.tuple([resourceIdSchema, resourceIdSchema]))
    .max((LIMITS.placesPerGroup * (LIMITS.placesPerGroup - 1)) / 2)
    .refine((pairs) => pairs.every(([a, b]) => a !== b), "Une paire, c'est deux personnes."),
});

export type ExclusionsInput = z.input<typeof exclusionsSchema>;

const colorSchema = z.enum(GROUP_COLOR_KEYS);

/**
 * The draw rules of the gommettes. By default a gommette's people do not draw each other;
 * `openColors` lifts that for some. `colorPairs`: two gommettes that do not draw each
 * other either (the greens and the pinks), both ways.
 */
export const colorRulesSchema = z.object({
  openColors: z.array(colorSchema).max(GROUP_COLOR_KEYS.length),
  colorPairs: z
    .array(z.tuple([colorSchema, colorSchema]))
    .max((GROUP_COLOR_KEYS.length * (GROUP_COLOR_KEYS.length - 1)) / 2)
    .refine((pairs) => pairs.every(([a, b]) => a !== b), "Deux gommettes différentes."),
});
export type ColorRules = z.infer<typeof colorRulesSchema>;
export type ColorRulesInput = z.input<typeof colorRulesSchema>;

/**
 * A redraw after some participants saw their result: the client echoes how many it warned
 * about, so a redraw never discards more than the organizer agreed to.
 */
export const drawSchema = z.object({ confirmRevealed: z.number().int().min(0).optional() });
export type DrawInput = z.input<typeof drawSchema>;

export const duplicateGroupSchema = z.object({
  title: groupTitleSchema,
  eventDate: z.iso.date().nullable().optional(),
});
export type DuplicateGroupInput = z.input<typeof duplicateGroupSchema>;

export const unsubscribeSchema = z.object({ token: z.string().min(10).max(600) });
